import { AfterViewChecked, Component, ElementRef, EventEmitter, Input, OnChanges, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

const MAX_RENDERED_LINES = 5000;
const STICK_TO_BOTTOM_SLACK_PX = 24;

export interface ViewerLine {
    number: number;
    text: string;
    match: boolean;
}

@Component({
    standalone: true,
    selector: 'text-viewer',
    imports: [CommonModule, FormsModule],
    templateUrl: './text-viewer.component.html',
    styleUrls: ['./text-viewer.component.css']})
export class TextViewerComponent implements OnChanges, AfterViewChecked {
    @Input() text: string;
    @Input() filename = 'text.txt';
    @Input() wrap = false;
    @Input() follow = false;
    @Input() startLine = 1;

    @Output() followChange = new EventEmitter<boolean>();

    @ViewChild('tvBody') tvBody: ElementRef<HTMLElement>;

    private pendingScroll = false;
    private lastAppliedScrollTop = -1;

    query = '';
    onlyMatches = false;

    lines: ViewerLine[] = [];
    totalLines = 0;
    matchCount = 0;
    truncated = false;

    ngOnChanges() {
        this.apply();
        if (this.follow) {
            this.pendingScroll = true;
        }
    }

    ngAfterViewChecked() {
        if (!this.pendingScroll || !this.follow || !this.tvBody) {
            return;
        }
        this.pendingScroll = false;
        const body = this.tvBody.nativeElement;
        if (body.scrollHeight - body.scrollTop - body.clientHeight <= 1) {
            return;
        }
        body.scrollTop = body.scrollHeight;
        this.lastAppliedScrollTop = body.scrollTop;
    }

    onBodyScroll() {
        if (!this.follow || !this.tvBody) {
            return;
        }
        const body = this.tvBody.nativeElement;
        if (body.scrollTop === this.lastAppliedScrollTop) {
            return;
        }
        if (body.scrollHeight - body.scrollTop - body.clientHeight > STICK_TO_BOTTOM_SLACK_PX) {
            this.follow = false;
            this.followChange.emit(false);
        }
    }

    trackLine(index: number, line: ViewerLine): string {
        return line.number + ':' + line.text;
    }

    apply() {
        const raw = (this.text || '').split(/\r?\n/);
        this.totalLines = raw.length;

        const needle = this.query.trim().toLowerCase();
        const collected: ViewerLine[] = [];
        let matches = 0;

        for (let i = 0; i < raw.length; i++) {
            const match = !!needle && raw[i].toLowerCase().includes(needle);
            if (match) {
                matches++;
            }
            if (this.onlyMatches && needle && !match) {
                continue;
            }
            if (collected.length < MAX_RENDERED_LINES) {
                collected.push({ number: this.startLine + i, text: raw[i], match });
            }
        }

        this.matchCount = matches;
        this.truncated = collected.length >= MAX_RENDERED_LINES;
        this.lines = collected;
    }

    clear() {
        this.query = '';
        this.onlyMatches = false;
        this.apply();
    }

    copy() {
        const textarea = document.createElement('textarea');
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        textarea.value = this.text || '';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
    }

    download() {
        const blob = new Blob([this.text || ''], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = this.filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
    }
}
